// Pure pointer→bar-index math for the SVG scrub charts (MiniChart's 1D view,
// PopupChart's 1W+ view, and anything else that renders a crosshair readout on
// a viewBox-driven SVG). Lives here so the hit-testing is identical across
// those charts and testable without a browser.

export interface ScrubGeometry {
  /** The SVG's viewBox width. */
  viewW: number;
  /** Left edge of the plot area in viewBox units (0 when there's no axis gutter). */
  padLeft: number;
  /** Plot-area width in viewBox units. */
  plotW: number;
  /** Number of bars plotted evenly across `plotW`. */
  count: number;
}

/** The subset of DOMRect the hit-test needs — keeps this callable from tests. */
export interface ScrubRect {
  left: number;
  width: number;
}

/**
 * Bar index under a pointer's `clientX`, or `null` when there's nothing to
 * hit-test.
 *
 * `clamp` distinguishes the two gestures that drive a crosshair:
 *   - a press-drag (finger or held mouse button) clamps to the nearest end
 *     bar, so the readout survives a finger sliding past the chart edge
 *     mid-scrub instead of blinking out;
 *   - a passive mouse hover returns `null` outside the plot area, so the
 *     crosshair clears when the cursor drifts into the axis gutter.
 */
export function scrubIndexFromClientX(
  clientX: number,
  rect: ScrubRect,
  geom: ScrubGeometry,
  clamp = false,
): number | null {
  const { viewW, padLeft, plotW, count } = geom;
  if (count < 1 || rect.width <= 0 || plotW <= 0 || viewW <= 0) return null;
  if (count === 1) return 0;
  // The SVG is stretched with preserveAspectRatio="none", so map through the
  // element's live screen rect rather than assuming viewBox units are pixels.
  const xPx = ((clientX - rect.left) / rect.width) * viewW;
  const t = (xPx - padLeft) / plotW;
  if (!clamp && (t < 0 || t > 1)) return null;
  const idx = Math.round(Math.max(0, Math.min(1, t)) * (count - 1));
  return Math.max(0, Math.min(count - 1, idx));
}
